import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../redux/store";
import {
  fetchHolidays,
  addHoliday,
  editHoliday,
  deleteHolidayById,
} from "../redux/actions/holidayAction";
import { Holiday } from "../types/Holiday";
import { StoreState } from "../redux/reducers";
import HolidayForm from "../components/HolidayForm";
import HolidayList from "../components/HolidayList";
import Modal from "../components/Modal";
import CollapseCard from "../components/custom/CollapseCard";
import PageContainer from "../components/custom/PageContainer";
import Main from "../components/custom/Main";
import { useToast } from "../components/toast/ToastProvider";
import HolidayFilter from "../components/HolidayFilter";

const HolidayPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const toast = useToast();

  const holidays = useSelector((state: StoreState) => state.holiday);
  const [selectedHoliday, setSelectedHoliday] = useState<Holiday | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    dispatch(fetchHolidays()).finally(() => setLoading(false));
  }, [dispatch]);

  const handleSave = async (holiday: Holiday) => {
    setLoading(true);
    try {
      if (selectedHoliday) {
        await dispatch(editHoliday(holiday.id, holiday));
        toast("Holiday updated successfully");
      } else {
        await dispatch(addHoliday(holiday));
        toast("Holiday added successfully");
      }
      await dispatch(fetchHolidays());
      setSelectedHoliday(null);
      setShowModal(false);
    } catch (error) {
      toast("Error saving holiday");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("Are you sure you want to delete this holiday?")) {
      setLoading(true);
      try {
        await dispatch(deleteHolidayById(id));
        toast("Holiday deleted successfully");
        await dispatch(fetchHolidays());
      } catch (error) {
        toast("Error deleting holiday");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEdit = (holiday: Holiday) => {
    setSelectedHoliday(holiday);
    setShowModal(true);
  };
  const [filteredHolidays, setFilteredHolidays] = useState<Holiday[]>([]);

  const [selectedYear, setSelectedYear] = useState(
    new Date().getFullYear().toString(),
  );
  useEffect(() => {
    const storedYear = localStorage.getItem("holidayFilterYear");
    if (storedYear) {
      setSelectedYear(storedYear);
      const filtered = holidays.filter((h) => h.year === storedYear);
      setFilteredHolidays(filtered);
    } else {
      const currentYear = new Date().getFullYear().toString();
      setSelectedYear(currentYear);
      setFilteredHolidays(holidays.filter((h) => h.year === currentYear));
    }
  }, [holidays]);

  const handleFind = () => {
    localStorage.setItem("holidayFilterYear", selectedYear); // Store selected year
    const filtered = holidays.filter((h) =>
      selectedYear ? h.year === selectedYear : true,
    );

    if (filtered.length === 0) {
      toast("No such data found");
    }

    setFilteredHolidays(filtered);
  };

  const handleReset = () => {
    const currentYear = new Date().getFullYear().toString();
    localStorage.removeItem("holidayFilterYear");
    setSelectedYear(currentYear);
    setFilteredHolidays([]);

    const filtered = holidays.filter((h) => h.year === currentYear);
    setFilteredHolidays(filtered);
  };

  return (
    <Main>
      <PageContainer
        title="Holiday Management"
        onButtonClick={() => setShowModal(true)}
        buttonText={loading ? "Loading..." : "ADD"}
      >
        <Modal
          visible={showModal}
          size="xl"
          onClose={() => {
            setShowModal(false);
            setSelectedHoliday(null);
          }}
          headerText={selectedHoliday ? "Update Holiday" : "Add Holiday"}
        >
          <HolidayForm
            initialData={selectedHoliday}
            onSave={handleSave}
            clearAfterSubmit={() => setSelectedHoliday(null)}
          />
        </Modal>
        <CollapseCard title="Holiday Filter">
          <HolidayFilter
            selectedYear={selectedYear}
            onYearChange={(year) => setSelectedYear(year)}
            onFind={handleFind}
            onReset={handleReset}
          />
        </CollapseCard>

        <CollapseCard title="Holiday List">
          <HolidayList
            holidays={filteredHolidays}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </CollapseCard>
      </PageContainer>
    </Main>
  );
};

export default HolidayPage;
